import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  animationFrameScheduler,
  defaultIfEmpty,
  filter,
  finalize,
  fromEvent,
  last,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useLabelingBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { ThreeState } from '@/type';
import type { NEW__LabelingBox } from '@/util';
import { filterOrRestart, subVector, withCanvasXY, withTarget } from '@/util';

import {
  applyMoveOffsetToBox,
  getIntersectPlanePointByNDC,
  isFirstIntersectedBoxEqaul,
  isIntersected,
  isTopMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
  withIntersectObject,
  withIntersectPlanePoint,
} from '../util';

const useDefaultViewTopMultiMove = ({
  setRenderBoxListById,
  disabled,
  isMultiMoveRef,
  isSingleMoveRef,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
  isMultiMoveRef: React.RefObject<boolean>;
  isSingleMoveRef: React.RefObject<boolean>;
}) => {
  const getThree = useThree(state => state.get as () => ThreeState);
  const { labelingBoxList } = useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);

  const getSelectedLabelingBoxList = () => {
    const selectedBoxIdMap = useLabelingBoxStore.getState().selectedBoxIdMap;

    return labelingBoxListRef.current.filter(box => selectedBoxIdMap[box.id]);
  };

  const prepareMultiMove = () => {
    const selectedLabelingBoxList = getSelectedLabelingBoxList();

    setControlEnabled(false, getThree().controls);

    setRenderBoxListById(selectedLabelingBoxList.map(v => v.id));
    isMultiMoveRef.current = true;
    isSingleMoveRef.current = false;
  };

  const cleanupMultiMove = () => {
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, getThree().controls);
    setRenderBoxListById([]);
    isMultiMoveRef.current = false;
  };

  const cleanupOnInvalidStream = () => {
    isMultiMoveRef.current = false;
    if (isSingleMoveRef.current) return;
  };

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  useSubscriptions(() => {
    if (disabled) {
      cleanupMultiMove();
      cleanupOnInvalidStream();

      return [];
    }

    const isAiAssistRunning = useToolMenuStore
      .getState()
      .isSelectedByToolName('aiAssist');

    return [
      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(() => isTopMode(useToolMainCameraStore.getState().getMode())),
          filter(() => !isAiAssistRunning),
          filter(
            () =>
              !useEditingLabelBoxStore.getState().isTransforming &&
              !useEditingLabelBoxStore.getState().isResizing,
          ),
          filter(() => getSelectedLabelingBoxList().length > 0),
          filter(
            e =>
              validMouseEventAndCanvasElement(e) ||
              (e.target instanceof HTMLDivElement &&
                !!e.target.ariaLabel?.match(/box-track-id/)),
          ),
          withTarget,
          withCanvasXY,
          map(e => {
            const selectedLabelingBoxList = getSelectedLabelingBoxList();

            return withIntersectObject(
              getThree().camera,
              selectedLabelingBoxList,
            )(e);
          }),
          filterOrRestart(({ intersections }) => isIntersected(intersections)),
          filter(({ intersections: selectedIntersections, event }) => {
            const getIntersectLabelingBoxList = withIntersectObject(
              getThree().camera,
              labelingBoxListRef.current,
            );
            const { intersections } = getIntersectLabelingBoxList(event);

            return isFirstIntersectedBoxEqaul(
              intersections,
              selectedIntersections,
            );
          }),
          map(({ intersections, event }) => {
            const mouseDownPosition = withIntersectPlanePoint({
              event,
              camera: getThree().camera,
              coplanarPoint: intersections[0].point,
            });
            return { intersections, event, mouseDownPosition };
          }),
          tap(prepareMultiMove),
          switchMap(
            ({ intersections: mouseDownIntersections, mouseDownPosition }) =>
              fromEvent<MouseEvent>(window, 'mousemove').pipe(
                withTarget,
                filterOrRestart(e => e.target instanceof HTMLCanvasElement),
                withCanvasXY,
                map(mouseMoveEvent => {
                  const mouseMovePosition = getIntersectPlanePointByNDC({
                    ndc: new Vector2(
                      mouseMoveEvent.canvasX,
                      mouseMoveEvent.canvasY,
                    ),
                    camera: getThree().camera,
                    coplanarPoint: mouseDownIntersections[0].point,
                  });

                  return { mouseMovePosition };
                }),
                map(({ mouseMovePosition }) => {
                  const moveOffset = subVector(
                    mouseMovePosition,
                    mouseDownPosition,
                  );

                  return { moveOffset, mouseDownIntersections };
                }),
                filterOrRestart(
                  ({ moveOffset }) =>
                    !isNaN(moveOffset.x) && !isNaN(moveOffset.y),
                ),
                tap(({ moveOffset }) => {
                  useEditingLabelBoxStore
                    .getState()
                    .setMoveOffset([moveOffset.x, moveOffset.y, 0]);
                }),
                takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
                defaultIfEmpty({ moveOffset: new Vector3() }),
                last(),
                map(({ moveOffset }) => {
                  const selectedLabelingBoxList = getSelectedLabelingBoxList();

                  useLabelingBoxStore.getState().moveBoxList(
                    selectedLabelingBoxList.map(box =>
                      applyMoveOffsetToBox(moveOffset, {
                        x: box.position[0],
                        y: box.position[1],
                        z: box.position[2],
                      })(box.id),
                    ),
                  );

                  return selectedLabelingBoxList;
                }),
                observeOn(animationFrameScheduler),
                finalize(cleanupMultiMove),
              ),
          ),
          finalize(cleanupOnInvalidStream),
        )
        .subscribe(),
    ];
  }, [disabled]);
};

export default useDefaultViewTopMultiMove;
